/**
 * The two icons on the checkout screen.
 *
 * Inline SVG rather than emoji. An emoji renders as a full-colour cartoon on Apple, a flat glyph
 * on Android, and sometimes as a replacement box on a desktop browser — on the one screen that
 * handles money, a control that looks different on every device looks broken on most of them.
 *
 * Every icon is `aria-hidden`: the control around it carries the label, so a screen reader hears a
 * name rather than a shape.
 */

const base = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** Enter a different amount. */
export function PencilIcon() {
  return (
    <svg {...base}>
      <path d="M4 20h4L19 9a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5V20Z" />
      <path d="M14.5 6.5 17.5 9.5" />
    </svg>
  );
}

/** Recognition is on: this gift will carry a name. */
export function PersonIcon() {
  return (
    <svg {...base}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  );
}

/**
 * Recognition is off: the gift is anonymous.
 *
 * A masked figure rather than a crossed-out one — anonymity is the default and the ordinary
 * choice here, not a feature that has been disabled.
 */
export function AnonymousIcon() {
  return (
    <svg {...base}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
      <path d="M7.5 7.5h9" />
    </svg>
  );
}

/**
 * A note to the organization, on the give card's additions line (owner 2026-10-10). A speech
 * bubble with lines — iOS draws `text.bubble` on the same control.
 */
export function NoteIcon() {
  return (
    <svg {...base}>
      <path d="M20 15a2 2 0 0 1-2 2H8l-4 3V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" />
      <path d="M8 8.5h8" />
      <path d="M8 12h5.5" />
    </svg>
  );
}

/**
 * What the gift supports, behind one icon on the additions line (owner 2026-10-10: the chooser is
 * "not in the card by default"). A folder — iOS draws `folder` on the same control.
 */
export function FolderIcon() {
  return (
    <svg {...base}>
      <path d="M3.5 7A2.5 2.5 0 0 1 6 4.5h3.5l2 2.5H18A2.5 2.5 0 0 1 20.5 9.5v7A2.5 2.5 0 0 1 18 19h-12A2.5 2.5 0 0 1 3.5 16.5Z" />
    </svg>
  );
}

/**
 * The give button's chooser segment (owner 2026-10-10): a gear, not a card, because the sheet
 * behind it holds more than payment methods — a pledge and a fund grant live there too. iOS draws
 * `gearshape` on the same control.
 */
export function GearIcon() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 4.2v2M12 17.8v2M19.8 12h-2M6.2 12h-2M17.5 6.5l-1.4 1.4M7.9 16.1l-1.4 1.4M17.5 17.5l-1.4-1.4M7.9 7.9 6.5 6.5" />
    </svg>
  );
}

/** Find an organization near me / return to the search screen. */
export function LocationIcon() {
  return (
    <svg {...base}>
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

/** Checking in at a door — the stranger's half of the directory's trailing control. */
export function TicketIcon() {
  return (
    <svg {...base}>
      <rect x="3" y="6" width="18" height="12" rx="2.5" />
      <path d="M3 11h18" />
    </svg>
  );
}

/** More about this organization and event. */
export function InfoIcon() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 7.75v.5" />
    </svg>
  );
}

/** Share this organization or event (GH-864e): the platform's own "share" shape, a tray and arrow. */
export function ShareIcon() {
  return (
    <svg {...base}>
      <path d="M12 3v12" />
      <path d="m7.5 7.5 4.5-4.5 4.5 4.5" />
      <path d="M8 11H6.5A1.5 1.5 0 0 0 5 12.5v6A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-6a1.5 1.5 0 0 0-1.5-1.5H16" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg {...base}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </svg>
  );
}

export function BackIcon() {
  return (
    <svg {...base}>
      <path d="M15 19 8 12l7-7" />
    </svg>
  );
}

/** The donor page menu: what this event is running, and the quiet site links. */
export function MenuIcon() {
  return (
    <svg {...base}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

/**
 * Asking the room's speakers a question.
 *
 * A bubble with a question mark in it rather than a bare "?": the mark alone is what an app draws
 * for help, and this control does not open help — it opens the queue of questions the hall is
 * raising hands behind. iOS reaches for `questionmark.bubble` on the same row for the same reason.
 */
export function QuestionIcon() {
  return (
    <svg {...base}>
      <path d="M20 15a2 2 0 0 1-2 2H8l-4 3V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" />
      <path d="M10 9a2 2 0 1 1 2 2v1.5" />
    </svg>
  );
}

/** The comment wall: what the room is writing, rather than what it is asking. */
export function CommentIcon() {
  return (
    <svg {...base}>
      <path d="M20 15a2 2 0 0 1-2 2H8l-4 3V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" />
      <path d="M8 9h8" />
      <path d="M8 12.5h5" />
    </svg>
  );
}

/**
 * Live trivia (GH-912): a question mark on a game tile, so the row reads as "something to answer,
 * right now" rather than borrowing the engagement wall's `QuestionIcon` — that one is a donor
 * asking the room something, and this is the room asking the donor.
 */
export function TriviaIcon() {
  return (
    <svg {...base}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.3 2.36c-.63.24-.8.5-.8 1.14v.5" />
      <path d="M12 16.5v.25" />
    </svg>
  );
}

/**
 * A poll that is open for votes.
 *
 * Bars rather than the amber dot the design sketches, and the dot survives beside it: the dot is
 * *state* ("this one is open"), the glyph is *kind* ("this is a poll"), and collapsing the two
 * would leave a closed poll with nothing identifying it but its title.
 */
export function PollIcon() {
  return (
    <svg {...base}>
      <path d="M6 20V11" />
      <path d="M12 20V4" />
      <path d="M18 20v-6" />
    </svg>
  );
}
